import React from "react";

type Scale = "xl" | "lg" | "md" | "sm" | "headline";
type Tag = "h1" | "h2" | "h3" | "h4" | "p" | "span" | "div";

interface DisplayProps {
  as?: Tag;
  scale?: Scale;
  /** When provided, this word inside `children` is rendered in italic Fraunces. */
  italicWord?: string;
  className?: string;
  children: React.ReactNode;
  id?: string;
}

const SCALE: Record<Scale, string> = {
  xl:       "text-4xl sm:text-5xl lg:text-display-xl",
  lg:       "text-4xl sm:text-display-md lg:text-display-lg",
  md:       "text-3xl sm:text-display-sm lg:text-display-md",
  sm:       "text-2xl sm:text-3xl lg:text-display-sm",
  headline: "text-xl sm:text-2xl lg:text-headline",
};

function renderWithItalic(children: React.ReactNode, italicWord?: string): React.ReactNode {
  if (!italicWord || typeof children !== "string") return children;
  const idx = children.toLowerCase().indexOf(italicWord.toLowerCase());
  if (idx === -1) return children;
  const before = children.slice(0, idx);
  const word = children.slice(idx, idx + italicWord.length);
  const after = children.slice(idx + italicWord.length);
  return (
    <>
      {before}
      <span className="italic font-display-italic">{word}</span>
      {after}
    </>
  );
}

export function Display({
  as: Tag = "h2",
  scale = "md",
  italicWord,
  className = "",
  children,
  ...rest
}: DisplayProps) {
  const cls = [
    "font-display",
    SCALE[scale],
    "text-coffee-900 dark:text-cream-50",
    "tracking-tight",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return React.createElement(
    Tag,
    { className: cls, ...rest },
    renderWithItalic(children, italicWord)
  );
}

export default Display;
